/**
 * MemberManagement component
 * Modal for managing project members (owner only)
 */

import { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import Modal from '../common/Modal'
import Button from '../common/Button'
import LoadingSpinner from '../common/LoadingSpinner'
import MemberList from './MemberList'
import UserSearchCombobox from './UserSearchCombobox'
import projectsService from '../../services/projects'
import toast from 'react-hot-toast'

const MemberManagement = ({
  isOpen,
  onClose,
  projectId,
  userRole,
  currentUserId,
  onMemberCountChange,
}) => {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(false)
  const [addingMember, setAddingMember] = useState(false)
  const [selectedRole, setSelectedRole] = useState('editor')
  const [removingMemberId, setRemovingMemberId] = useState(null)

  const loadMembers = useCallback(async () => {
    try {
      setLoading(true)
      const data = await projectsService.listMembers(projectId)
      setMembers(data.results || data)
    } catch (err) {
      console.error('Error loading members:', err)
      toast.error('Failed to load members')
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    if (isOpen) {
      loadMembers()
    }
  }, [isOpen, loadMembers])

  const handleAddMember = async (user) => {
    try {
      setAddingMember(true)

      // Optimistic update
      const tempMember = {
        id: `temp-${Date.now()}`,
        role: selectedRole,
        user: user,
        joined_at: new Date().toISOString(),
      }
      setMembers((prev) => [...prev, tempMember])

      // Add member via API
      const newMember = await projectsService.addMember(projectId, user.id, selectedRole)

      // Replace temp member with real member
      setMembers((prev) => {
        const updated = prev.map((m) => (m.id === tempMember.id ? newMember : m))
        // Notify parent of count change
        if (onMemberCountChange) {
          onMemberCountChange(updated.length)
        }
        return updated
      })

      toast.success(`${user.full_name} added as ${selectedRole}`)
    } catch (err) {
      console.error('Error adding member:', err)

      // Rollback optimistic update
      setMembers((prev) => prev.filter((m) => !m.id.startsWith('temp-')))

      if (err.response?.status === 400) {
        const errorData = err.response.data
        if (errorData.user_id) {
          toast.error(errorData.user_id[0] || 'User is already a member')
        } else {
          toast.error('Failed to add member')
        }
      } else {
        toast.error('Failed to add member')
      }
    } finally {
      setAddingMember(false)
    }
  }

  const handleRemoveMember = (membershipId) => {
    // Show inline confirmation
    setRemovingMemberId(membershipId)
  }

  const confirmRemoveMember = async (membershipId) => {
    try {
      // Optimistic update
      setMembers((prev) => {
        const updated = prev.filter((m) => m.id !== membershipId)
        // Notify parent of count change
        if (onMemberCountChange) {
          onMemberCountChange(updated.length)
        }
        return updated
      })
      setRemovingMemberId(null)

      await projectsService.removeMember(projectId, membershipId)
      toast.success('Member removed')
    } catch (err) {
      console.error('Error removing member:', err)

      // Rollback on error
      await loadMembers()
      toast.error('Failed to remove member')
    }
  }

  const cancelRemoveMember = () => {
    setRemovingMemberId(null)
  }

  const handleUpdateRole = async (membershipId, newRole) => {
    const member = members.find((m) => m.id === membershipId)
    if (!member) return

    const oldRole = member.role

    try {
      // Optimistic update
      setMembers((prev) => prev.map((m) => (m.id === membershipId ? { ...m, role: newRole } : m)))

      await projectsService.updateMemberRole(projectId, membershipId, newRole)
      toast.success('Member role updated')
    } catch (err) {
      console.error('Error updating role:', err)

      // Rollback on error
      setMembers((prev) => prev.map((m) => (m.id === membershipId ? { ...m, role: oldRole } : m)))
      toast.error('Failed to update role')
    }
  }

  const isOwner = userRole === 'owner'
  const memberUserIds = members.map((m) => m.user.id)

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Manage Members" size="lg">
      <div className="space-y-6">
        {/* Add Member Section (Owner Only) */}
        {isOwner && (
          <div className="pb-6 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Add Member</h3>
            <div className="space-y-3">
              <UserSearchCombobox
                onSelectUser={handleAddMember}
                excludeUserIds={memberUserIds}
                placeholder="Search by name or email..."
              />
              <div className="flex items-center gap-2">
                <label htmlFor="role-select" className="text-sm text-gray-700">
                  Role:
                </label>
                <select
                  id="role-select"
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  disabled={addingMember}
                >
                  <option value="editor">Editor (can upload and analyze)</option>
                  <option value="viewer">Viewer (read-only)</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Members List */}
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-3">
            Current Members ({members.length})
          </h3>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No members found</p>
            </div>
          ) : (
            <MemberList
              members={members}
              userRole={userRole}
              currentUserId={currentUserId}
              onRemoveMember={handleRemoveMember}
              onUpdateRole={handleUpdateRole}
              removingMemberId={removingMemberId}
              onConfirmRemove={confirmRemoveMember}
              onCancelRemove={cancelRemoveMember}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  )
}

MemberManagement.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  userRole: PropTypes.oneOf(['owner', 'editor', 'viewer']).isRequired,
  currentUserId: PropTypes.number,
  onMemberCountChange: PropTypes.func,
}

export default MemberManagement
